import React, { useState, useMemo } from 'react';
import { Card, Button, Input } from '../ui';
import { Trophy, Plus, Trash2, ArrowUp, RefreshCw, BarChart2 } from 'lucide-react';

interface TeamRecord {
  id: string;
  name: string;
  wins: number;
  losses: number;
  ties: number;
  pointsFor: number;
  pointsAgainst: number;
}

const DEFAULT_TEAMS: TeamRecord[] = [
  { id: '1', name: 'Spartans', wins: 8, losses: 2, ties: 0, pointsFor: 240, pointsAgainst: 180 },
  { id: '2', name: 'Trojans', wins: 8, losses: 2, ties: 0, pointsFor: 220, pointsAgainst: 160 }, // Tied with Spartans on wins, Trojans have higher point diff (60 vs 60? PF-PA Trojan = 60, Spartan = 60. Tied on diff too!)
  { id: '3', name: 'Knights', wins: 6, losses: 4, ties: 0, pointsFor: 190, pointsAgainst: 170 },
  { id: '4', name: 'Titans', wins: 4, losses: 5, ties: 1, pointsFor: 150, pointsAgainst: 170 },
  { id: '5', name: 'Vikings', wins: 4, losses: 5, ties: 1, pointsFor: 130, pointsAgainst: 190 } // Tied with Titans on wins/ties, Titans have higher point diff
];

export function LeagueStandingsTiebreaker() {
  const [teams, setTeams] = useState<TeamRecord[]>(DEFAULT_TEAMS);
  const [tiebreakerRules, setTiebreakerRules] = useState<string[]>([
    'Point Differential',
    'Points For',
    'Points Against'
  ]);

  // Form states
  const [teamName, setTeamName] = useState('');
  const [wins, setWins] = useState('0');
  const [losses, setLosses] = useState('0');
  const [ties, setTies] = useState('0');
  const [pointsFor, setPointsFor] = useState('0');
  const [pointsAgainst, setPointsAgainst] = useState('0');

  const handleAddTeam = (e: React.FormEvent) => {
    e.preventDefault();
    if (!teamName.trim()) return;

    setTeams(prev => [
      ...prev,
      {
        id: `t_${Date.now()}`,
        name: teamName.trim(),
        wins: parseInt(wins) || 0,
        losses: parseInt(losses) || 0,
        ties: parseInt(ties) || 0,
        pointsFor: parseInt(pointsFor) || 0,
        pointsAgainst: parseInt(pointsAgainst) || 0
      }
    ]);

    setTeamName('');
    setWins('0');
    setLosses('0');
    setTies('0');
    setPointsFor('0');
    setPointsAgainst('0');
  };

  const loadSample = () => {
    setTeams(DEFAULT_TEAMS);
  };

  const clearAll = () => {
    setTeams([]);
  };

  // Drag-and-drop or click to prioritize rule
  const handlePrioritizeRule = (ruleIndex: number) => {
    if (ruleIndex === 0) return; // Already top priority
    setTiebreakerRules(prev => {
      const copy = [...prev];
      const selected = copy[ruleIndex];
      // Move selected rule to the top
      copy.splice(ruleIndex, 1);
      return [selected, ...copy];
    });
  };

  // Main Sorting algorithm integrating custom tiebreaker rules
  const sortedStandings = useMemo(() => {
    if (teams.length === 0) return [];

    return [...teams].sort((a, b) => {
      // 1. Calculate main Win Percentage first
      const aGames = a.wins + a.losses + a.ties;
      const bGames = b.wins + b.losses + b.ties;
      const aWinPercent = aGames > 0 ? (a.wins + 0.5 * a.ties) / aGames : 0;
      const bWinPercent = bGames > 0 ? (b.wins + 0.5 * b.ties) / bGames : 0;

      if (bWinPercent !== aWinPercent) {
        return bWinPercent - aWinPercent;
      }

      // If tied, loop through the user-specified tiebreaker rule priority list
      for (const rule of tiebreakerRules) {
        if (rule === 'Point Differential') {
          const aDiff = a.pointsFor - a.pointsAgainst;
          const bDiff = b.pointsFor - b.pointsAgainst;
          if (bDiff !== aDiff) return bDiff - aDiff;
        }
        if (rule === 'Points For') {
          if (b.pointsFor !== a.pointsFor) return b.pointsFor - a.pointsFor;
        }
        if (rule === 'Points Against') {
          // In points against, lower is better
          if (b.pointsAgainst !== a.pointsAgainst) return a.pointsAgainst - b.pointsAgainst;
        }
      }

      // Final tiebreaker: alphabet
      return a.name.localeCompare(b.name);
    });
  }, [teams, tiebreakerRules]);

  return (
    <div className="space-y-8">
      {/* Intro Banner */}
      <div className="bg-[#FFD600] border-4 border-black text-black p-6 shadow-[4px_4px_0px_#000000]">
        <h3 className="font-display font-black text-2xl uppercase tracking-tight mb-2">Sports League Standings & Tiebreaker Modeler</h3>
        <p className="font-bold text-sm max-w-xl">
          Build league tables and resolve ties automatically based on customizable cascade guidelines (Point Diff, Points For, Points Against).
        </p>
        <div className="flex gap-3 mt-4">
          <Button onClick={loadSample} variant="secondary" className="bg-black text-white hover:bg-[#0044FF] border-2 border-black rounded-none uppercase font-mono tracking-widest text-[10px] font-bold">
            <RefreshCw size={12} className="mr-2" /> Load Demo
          </Button>
          <Button onClick={clearAll} variant="ghost" className="border-2 border-black text-black hover:bg-red-600 hover:text-white rounded-none uppercase font-mono tracking-widest text-[10px] font-bold">
            Reset
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-8 text-sm">
        
        {/* Rules & Forms Column */}
        <div className="lg:col-span-1 space-y-6">
          
          {/* Rules priority */}
          <div className="bg-white border-4 border-black p-6 shadow-[4px_4px_0px_#000000] dark:bg-black dark:border-white dark:shadow-[4px_4px_0px_#FFFFFF] space-y-3">
            <h4 className="font-display font-black text-lg uppercase tracking-tight">Tiebreaker Hierarchy</h4>
            <p className="text-xs text-gray-500">Click a rule below to move it to the primary tiebreaking priority.</p>
            <div className="space-y-2 pt-1 font-mono text-xs">
              {tiebreakerRules.map((rule, idx) => (
                <div 
                  key={rule}
                  onClick={() => handlePrioritizeRule(idx)}
                  className={`border-2 border-black p-3 cursor-pointer select-none transition-all flex items-center justify-between ${
                    idx === 0 ? 'bg-[#0044FF] text-white' : 'bg-[#F5F5F0] hover:bg-gray-100 dark:bg-slate-900 text-black dark:text-white'
                  }`}
                >
                  <span>{idx + 1}. {rule}</span>
                  {idx > 0 && <span className="text-[10px] uppercase font-bold text-gray-400">Promote ↑</span>}
                </div>
              ))}
            </div>
          </div>

          {/* Add Team form */}
          <div className="bg-white border-4 border-black p-6 shadow-[4px_4px_0px_#000000] dark:bg-black dark:border-white dark:shadow-[4px_4px_0px_#FFFFFF] space-y-4">
            <h4 className="font-display font-black text-lg uppercase tracking-tight flex items-center gap-2">
              <Trophy size={18} className="text-[#0044FF]" /> Add Team Record
            </h4>
            
            <form onSubmit={handleAddTeam} className="space-y-3">
              <div>
                <label className="text-[10px] uppercase font-bold text-gray-500 block mb-1">Team Name</label>
                <Input value={teamName} onChange={e => setTeamName(e.target.value)} placeholder="Wildcats" className="border-2 border-black rounded-none h-10 font-bold" />
              </div>
              
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-[10px] uppercase font-bold text-gray-500 block mb-1">Wins</label>
                  <Input type="number" value={wins} onChange={e => setWins(e.target.value)} className="border-2 border-black rounded-none h-10 font-mono font-bold" />
                </div>
                <div>
                  <label className="text-[10px] uppercase font-bold text-gray-500 block mb-1">Losses</label>
                  <Input type="number" value={losses} onChange={e => setLosses(e.target.value)} className="border-2 border-black rounded-none h-10 font-mono font-bold" />
                </div>
                <div>
                  <label className="text-[10px] uppercase font-bold text-gray-500 block mb-1">Ties</label>
                  <Input type="number" value={ties} onChange={e => setTies(e.target.value)} className="border-2 border-black rounded-none h-10 font-mono font-bold" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] uppercase font-bold text-gray-500 block mb-1">Points For (PF)</label>
                  <Input type="number" value={pointsFor} onChange={e => setPointsFor(e.target.value)} className="border-2 border-black rounded-none h-10 font-mono font-bold" />
                </div>
                <div>
                  <label className="text-[10px] uppercase font-bold text-gray-500 block mb-1">Points Against (PA)</label>
                  <Input type="number" value={pointsAgainst} onChange={e => setPointsAgainst(e.target.value)} className="border-2 border-black rounded-none h-10 font-mono font-bold" />
                </div>
              </div>

              <Button type="submit" className="w-full bg-[#0044FF] text-white border-2 border-black rounded-none font-bold uppercase tracking-wider py-2 hover:bg-black">
                Save Team
              </Button>
            </form>
          </div>

        </div>

        {/* Generated Standings output */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border-4 border-black p-6 shadow-[4px_4px_0px_#000000] dark:bg-black dark:border-white dark:shadow-[4px_4px_0px_#FFFFFF] space-y-4">
            <h4 className="font-display font-black text-xl uppercase tracking-tight border-b-4 border-black pb-2 dark:border-white">
              Official League Standings
            </h4>

            {teams.length === 0 ? (
              <p className="text-sm font-mono text-gray-400 py-8 border-2 border-dashed border-gray-300 text-center uppercase">Add teams to compute standings table.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs border-collapse">
                  <thead>
                    <tr className="bg-[#F5F5F0] border-b-4 border-black font-mono font-bold uppercase tracking-wider text-black dark:bg-slate-900 dark:border-white dark:text-white">
                      <th className="py-2 px-3 w-12 text-center">Rank</th>
                      <th className="py-2 px-3">Team Name</th>
                      <th className="py-2 px-3 text-center">W - L - T</th>
                      <th className="py-2 px-3 text-right">PCT</th>
                      <th className="py-2 px-3 text-right">PF</th>
                      <th className="py-2 px-3 text-right">PA</th>
                      <th className="py-2 px-3 text-right">DIFF</th>
                      <th className="py-2 px-3 text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y-2 divide-gray-200 dark:divide-slate-800 font-bold">
                    {sortedStandings.map((team, idx) => {
                      const games = team.wins + team.losses + team.ties;
                      const winPercent = games > 0 ? (team.wins + 0.5 * team.ties) / games : 0;
                      const diff = team.pointsFor - team.pointsAgainst;

                      return (
                        <tr key={team.id} className="hover:bg-[#FFD600]/10 transition-colors">
                          <td className="py-3 px-3 text-center font-mono border-r-2 border-gray-100 dark:border-slate-800">
                            <span className="inline-flex items-center justify-center w-6 h-6 border-2 border-black bg-black text-white dark:border-white dark:bg-white dark:text-black font-mono font-black text-xs">
                              {idx + 1}
                            </span>
                          </td>
                          <td className="py-3 px-3 font-display uppercase tracking-tight text-sm text-black dark:text-white">
                            {team.name}
                          </td>
                          <td className="py-3 px-3 text-center font-mono">
                            {team.wins} - {team.losses} - {team.ties}
                          </td>
                          <td className="py-3 px-3 text-right font-mono text-blue-600 dark:text-blue-400">
                            {winPercent.toFixed(3)}
                          </td>
                          <td className="py-3 px-3 text-right font-mono">{team.pointsFor}</td>
                          <td className="py-3 px-3 text-right font-mono">{team.pointsAgainst}</td>
                          <td className={`py-3 px-3 text-right font-mono ${diff >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {diff >= 0 ? '+' : ''}{diff}
                          </td>
                          <td className="py-3 px-3 text-center">
                            <button onClick={() => setTeams(prev => prev.filter(t => t.id !== team.id))} className="text-red-500 hover:text-red-700">
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

      </div>

    </div>
  );
}
